/**
 * Short vibrations that mark the seams of a conversation.
 *
 * In a voice-first app the screen is often not being looked at, so a turn
 * ending or a confirmation landing has to be reported through some channel that
 * is not sight. Touch is the cheapest one available.
 *
 * The catch is that `navigator.vibrate` does not exist in Safari on iOS, which
 * is a large share of the phones Namu runs on. So every call is feature
 * detected and every failure is silent: a missing buzz is a small loss, a thrown
 * error in the middle of a turn transition is a broken conversation. The
 * durations are deliberately at the short end. A haptic that outlasts the moment
 * it is marking reads as a malfunction.
 */

export type HapticPattern = 'tap' | 'turn' | 'confirm' | 'error'

const PATTERNS: Record<HapticPattern, number | number[]> = {
  /** A control was pressed. */
  tap: 10,
  /** The turn changed hands, Namu is listening or has stopped. */
  turn: 18,
  /** Something was saved or accepted. Two beats read as deliberate. */
  confirm: [12, 60, 12],
  /** Something went wrong. Longer and rougher, so it is not mistaken for confirm. */
  error: [30, 80, 30],
}

// Mirrors the `haptics` setting. Default on, matching the stored default, so a
// call before the settings store has been read still behaves as the user expects.
let enabled = true

export function setHapticsEnabled(value: boolean): void {
  enabled = value
}

export function isHapticsSupported(): boolean {
  if (typeof navigator === 'undefined') return false
  return typeof navigator.vibrate === 'function'
}

export function haptic(pattern: HapticPattern): void {
  if (!enabled) return
  if (!isHapticsSupported()) return

  try {
    navigator.vibrate(PATTERNS[pattern])
  } catch {
    // Some browsers throw when the document has not been interacted with yet,
    // or when a vibration policy blocks it. Nothing above this cares.
  }
}
